import * as vd from "virtual-dom";
import { AbortInfra3DSDKError } from "../../error/AbortInfra3DSDKError";
import { Spatial } from "../../geo/Spatial";
import { EdgeDirection } from "../../graph/edge/EdgeDirection";
import { DirectionDOMCalculator } from "./DirectionDOMCalculator";
import { isSpherical } from "../../geo/Geo";
import { SceneCamera } from "../../../infra3d/internal";
/**
 * @class DirectionDOMRenderer
 * @classdesc DOM renderer for direction arrows.
 */
export class DirectionDOMRenderer {
    constructor(configuration, size) {
        this._isEdge = false;
        this._calculator = new DirectionDOMCalculator(configuration, size);
        this._node = null;
        this._rotation = { phi: 0, theta: 0 };
        this._epsilon = (0.5 * Math.PI) / 180;
        this._sphericalLongitude = 0;
        this._highlightKey = null;
        this._distinguishSequence = false;
        this._needsRender = false;
        this._stepEdges = [];
        this._turnEdges = [];
        this._sphericalEdges = [];
        this._sequenceEdgeKeys = [];
        this._stepDirections = [
            EdgeDirection.StepForward,
            EdgeDirection.StepBackward,
            EdgeDirection.StepLeft,
            EdgeDirection.StepRight,
        ];
        this._turnDirections = [EdgeDirection.TurnLeft, EdgeDirection.TurnRight, EdgeDirection.TurnU];
        this._turnNames = {};
        this._turnNames[EdgeDirection.TurnLeft] = "mapillary-direction-turn-left";
        this._turnNames[EdgeDirection.TurnRight] = "mapillary-direction-turn-right";
        this._turnNames[EdgeDirection.TurnU] = "mapillary-direction-turn-around";
        // detects IE 8-11, then Edge 20+.
        const isIE = !!document.documentMode;
        this._isEdge = !isIE && !!window.StyleMedia;
    }
    /**
     * Get needs render.
     *
     * @returns {boolean} Value indicating whether render should be called.
     */
    get needsRender() {
        return this._needsRender;
    }
    /**
     * Renders virtual DOM elements.
     *
     * @description Calling render resets the needs render property.
     */
    render(navigator) {
        if (this._node == null) {
            return null;
        }
        this._needsRender = false;
        const rotation = this._rotation;
        const longitude = this._sphericalLongitude;
        let steps = [];
        let turns = [];
        // TOOD: Remove all of this isSpherical support if not needed.
        if (isSpherical(this._node.cameraType)) {
            steps = steps.concat(this._createSphericalArrows(navigator, rotation));
            turns = turns.concat(this._createTurnArrows(navigator, longitude));
        }
        else {
            steps = steps.concat(this._createPerspectiveToSphericalArrows(navigator, rotation));
            steps = steps.concat(this._createStepArrows(navigator, rotation));
            turns = turns.concat(this._createTurnArrows(navigator, longitude));
        }
        return this._getContainer(steps, turns, rotation);
    }
    setEdges(edgeStatus, sequence) {
        this._setEdges(edgeStatus, sequence);
        this._setNeedsRender();
    }
    /**
     * Set the render camera to use for calculating rotations.
     *
     * @param {Camera} camera
     */
    setRenderCamera(camera) {
        const rotation = this._computeRotation(camera);
        const slongitude = this._computeSphericalLongitude(camera);
        if (Math.abs(rotation.phi - this._rotation.phi) < this._epsilon &&
            Math.abs(rotation.theta - this._rotation.theta) < this._epsilon &&
            Math.abs(slongitude - this._sphericalLongitude) < this._epsilon) {
            return;
        }
        this._rotation = rotation;
        this._sphericalLongitude = slongitude;
        this._setNeedsRender();
    }
    _computeRotation(camera) {
        const orientation = camera.getOrientation();
        const latlon = camera.getLatLon();
        const _omega = Spatial.degToRad(orientation.x);
        const _phi = Spatial.degToRad(orientation.y);
        const x = -1 * Math.sin(_phi);
        const y = Math.sin(_omega) * Math.cos(_phi);
        const longitude = Spatial.degToRad(latlon[1]);
        const latitude = Spatial.degToRad(latlon[0]);
        const fov = Spatial.degToRad(latlon[2]);
        const phi = Math.atan2(y, x) - longitude;
        let theta = latitude - fov / 2;
        if (camera instanceof SceneCamera) {
            theta = Spatial.degToRad(-40);
        }
        return { phi: phi, theta: theta };
    }
    _computeSphericalLongitude(camera) {
        const latlon = camera.getLatLon();
        return Spatial.wrapAngle(Spatial.degToRad(latlon[1]));
    }
    /**
     * Set node for which to show edges.
     *
     * @param {Node} node
     */
    setNode(node) {
        this._node = node;
        this._clearEdges();
        this._setNeedsRender();
    }
    /**
     * Set configuration values.
     *
     * @param {IDirectionConfiguration} configuration
     */
    setConfiguration(configuration) {
        let needsRender = false;
        if (this._highlightKey !== configuration.highlightKey ||
            this._distinguishSequence !== configuration.distinguishSequence) {
            this._highlightKey = configuration.highlightKey;
            this._distinguishSequence = configuration.distinguishSequence;
            needsRender = true;
        }
        if (this._calculator.minWidth !== configuration.minWidth ||
            this._calculator.maxWidth !== configuration.maxWidth) {
            this._calculator.configure(configuration);
            needsRender = true;
        }
        if (needsRender) {
            this._setNeedsRender();
        }
    }
    /**
     * Detect the element's width and height and resize
     * elements accordingly.
     *
     * @param {ISize} size Size of vßiewer container element.
     */
    resize(size) {
        this._calculator.resize(size);
        this._setNeedsRender();
    }
    _setNeedsRender() {
        if (this._node != null) {
            this._needsRender = true;
        }
    }
    _clearEdges() {
        this._stepEdges = [];
        this._turnEdges = [];
        this._sphericalEdges = [];
        this._sequenceEdgeKeys = [];
    }
    _setEdges(edgeStatus, sequence) {
        this._stepEdges = [];
        this._turnEdges = [];
        this._sphericalEdges = [];
        this._sequenceEdgeKeys = [];
        for (const edge of edgeStatus.edges) {
            const direction = edge.data.direction;
            if (this._stepDirections.indexOf(direction) > -1) {
                this._stepEdges.push(edge);
                continue;
            }
            if (this._turnDirections.indexOf(direction) > -1) {
                this._turnEdges.push(edge);
                continue;
            }
            if (edge.data.direction === EdgeDirection.Spherical) {
                this._sphericalEdges.push(edge);
            }
        }
        if (this._distinguishSequence && sequence != null) {
            const edges = this._sphericalEdges.concat(this._stepEdges).concat(this._turnEdges);
            for (const edge of edges) {
                const edgeKey = edge.to;
                for (const sequenceKey of sequence.keys) {
                    if (sequenceKey === edgeKey) {
                        this._sequenceEdgeKeys.push(edgeKey);
                        break;
                    }
                }
            }
        }
    }
    _createSphericalArrows(navigator, rotation) {
        const arrows = [];
        for (const sphericalEdge of this._sphericalEdges) {
            arrows.push(this._createVNodeByKey(navigator, sphericalEdge.to, sphericalEdge.data.worldMotionAzimuth, rotation, this._calculator.outerRadius, "mapillary-direction-arrow-spherical"));
        }
        for (const stepEdge of this._stepEdges) {
            arrows.push(this._createSphericalToPerspectiveArrow(navigator, stepEdge.to, stepEdge.data.worldMotionAzimuth, rotation, stepEdge.data.direction));
        }
        return arrows;
    }
    _createSphericalToPerspectiveArrow(navigator, key, azimuth, rotation, direction) {
        const threshold = Math.PI / 8;
        let relativePhi = rotation.phi;
        switch (direction) {
            case EdgeDirection.StepBackward:
                relativePhi = rotation.phi - Math.PI;
                break;
            case EdgeDirection.StepLeft:
                relativePhi = rotation.phi + Math.PI / 2;
                break;
            case EdgeDirection.StepRight:
                relativePhi = rotation.phi - Math.PI / 2;
                break;
            default:
                break;
        }
        if (Math.abs(Spatial.wrapAngle(azimuth - relativePhi)) < threshold) {
            return this._createVNodeByKey(navigator, key, azimuth, rotation, this._calculator.outerRadius, "mapillary-direction-arrow-step");
        }
        return this._createVNodeInactive(key, azimuth, rotation);
    }
    _createPerspectiveToSphericalArrows(navigator, rotation) {
        const arrows = [];
        for (const sphericalEdge of this._sphericalEdges) {
            arrows.push(this._createVNodeByKey(navigator, sphericalEdge.to, sphericalEdge.data.worldMotionAzimuth, rotation, this._calculator.innerRadius, "mapillary-direction-arrow-spherical", true));
        }
        return arrows;
    }
    _createStepArrows(navigator, rotation) {
        const arrows = [];
        for (const stepEdge of this._stepEdges) {
            arrows.push(this._createVNodeByDirection(navigator, stepEdge.to, stepEdge.data.worldMotionAzimuth, rotation, stepEdge.data.direction));
        }
        return arrows;
    }
    _createTurnArrows(navigator, longitude) {
        const turns = [];
        if (longitude < -0.3 || longitude > 0.3) {
            return turns;
        }
        for (const turnEdge of this._turnEdges) {
            const direction = turnEdge.data.direction;
            const name = this._turnNames[direction];
            turns.push(this._createVNodeByTurn(navigator, turnEdge.to, name, direction));
        }
        return turns;
    }
    _createVNodeByKey(navigator, key, azimuth, rotation, offset, className, shiftVertically) {
        const onClick = (e) => {
            navigator.moveToKey$(key).subscribe(undefined, (error) => {
                if (!(error instanceof AbortInfra3DSDKError)) {
                    console.error(error);
                }
            });
        };
        return this._createVNode(key, azimuth, rotation, offset, className, "mapillary-direction-circle", onClick, shiftVertically);
    }
    _createVNodeByDirection(navigator, key, azimuth, rotation, direction) {
        const onClick = (e) => {
            navigator.moveDir$(direction).subscribe(undefined, (error) => {
                if (!(error instanceof AbortInfra3DSDKError)) {
                    console.error(error);
                }
            });
        };
        return this._createVNode(key, azimuth, rotation, this._calculator.outerRadius, "mapillary-direction-arrow-step", "mapillary-direction-circle", onClick);
    }
    _createVNodeByTurn(navigator, key, className, direction) {
        const onClick = (e) => {
            navigator.moveDir$(direction).subscribe(undefined, (error) => {
                if (!(error instanceof AbortInfra3DSDKError)) {
                    console.error(error);
                }
            });
        };
        const style = {
            height: this._calculator.turnCircleSizeCss,
            transform: "rotate(0)", // apply transform to preserve 3D
            width: this._calculator.turnCircleSizeCss,
        };
        switch (direction) {
            case EdgeDirection.TurnLeft:
                style.left = "5px";
                style.top = "50px";
                break;
            case EdgeDirection.TurnRight:
                style.right = "5px";
                style.top = "50px";
                break;
            case EdgeDirection.TurnU:
                style.left = "5px";
                style.bottom = "50px";
                break;
            default:
                break;
        }
        const circleProperties = {
            attributes: {
                "data-key": key,
            },
            onclick: onClick,
            style: style,
        };
        let circleClassName = "mapillary-direction-turn-circle";
        if (this._sequenceEdgeKeys.indexOf(key) > -1) {
            circleClassName += "-sequence";
        }
        if (this._highlightKey === key) {
            circleClassName += "-highlight";
        }
        const turn = vd.h(`div.${className}`, {}, []);
        return vd.h("div." + circleClassName, circleProperties, [turn]);
    }
    _createVNodeInactive(key, azimuth, rotation) {
        return this._createVNode(key, azimuth, rotation, this._calculator.outerRadius, "mapillary-direction-arrow-inactive", "mapillary-direction-circle-inactive");
    }
    _createVNode(key, azimuth, rotation, radius, className, circleClassName, onClick, shiftVertically) {
        const translation = this._calculator.angleToCoordinates(azimuth - rotation.phi);
        // rotate 90 degrees clockwise and flip over X-axis
        const translationX = Math.round(-radius * translation[1] + 0.5 * this._calculator.containerWidth);
        const translationY = Math.round(-radius * translation[0] + 0.5 * this._calculator.containerHeight);
        const shadowTranslation = this._calculator.relativeAngleToCoordinates(azimuth, rotation.phi);
        const shadowOffset = this._calculator.shadowOffset;
        const shadowTranslationX = -shadowOffset * shadowTranslation[1];
        const shadowTranslationY = shadowOffset * shadowTranslation[0];
        const filter = `drop-shadow(${shadowTranslationX}px ${shadowTranslationY}px 1px rgba(0,0,0,0.8))`;
        const properties = {
            style: {
                "-webkit-filter": filter,
                filter: filter,
            },
        };
        const chevron = vd.h("div." + className, properties, []);
        const azimuthDeg = -Spatial.radToDeg(azimuth - rotation.phi);
        const circleTransform = shiftVertically
            ? `translate(${translationX}px, ${translationY}px) rotate(${azimuthDeg}deg) translateZ(-0.01px)`
            : `translate(${translationX}px, ${translationY}px) rotate(${azimuthDeg}deg)`;
        const circleProperties = {
            attributes: { "data-key": key },
            onclick: onClick,
            style: {
                height: this._calculator.stepCircleSizeCss,
                marginLeft: this._calculator.stepCircleMarginCss,
                marginTop: this._calculator.stepCircleMarginCss,
                transform: circleTransform,
                width: this._calculator.stepCircleSizeCss,
            },
        };
        if (this._sequenceEdgeKeys.indexOf(key) > -1) {
            circleClassName += "-sequence";
        }
        if (this._highlightKey === key) {
            circleClassName += "-highlight";
        }
        return vd.h("div." + circleClassName, circleProperties, [chevron]);
    }
    _getContainer(steps, turns, rotation) {
        const thetaDeg = 90 - Spatial.radToDeg(-1 * rotation.theta);
        // edge does not handle hover on perspective transforms.
        const transform = this._isEdge
            ? "rotateX(${thetaDeg}deg)"
            : `perspective(${this._calculator.containerWidthCss}) rotateX(${thetaDeg}deg)`;
        const properties = {
            oncontextmenu: (event) => {
                event.preventDefault();
            },
            style: {
                bottom: this._calculator.containerBottomCss,
                height: this._calculator.containerHeightCss,
                left: this._calculator.containerLeftCss,
                marginLeft: this._calculator.containerMarginCss,
                transform: transform,
                width: this._calculator.containerWidthCss,
                "transform-origin": "bottom",
            },
        };
        return vd.h("div.mapillary-direction-perspective", properties, turns.concat(steps));
    }
}
//# sourceMappingURL=DirectionDOMRenderer.js.map